// src/hooks/useAuthData.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { USER_METADATA_NAMESPACE, FUNCTION_URL, FUNCTION_KEY } from '../utils/constants';

const getNamespacedClaim = (user, key) => {
  if (!user || !USER_METADATA_NAMESPACE) return undefined;
  return user[`${USER_METADATA_NAMESPACE}${key}`];
};

export const useAuthData = () => {
  const { isAuthenticated, user: auth0User, getAccessTokenSilently, isLoading: isAuth0Loading } = useAuth0();

  const [user, setUser] = useState(null); // Local state to enhance the user object
  const [decodedToken, setDecodedToken] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [isValidated, setIsValidated] = useState(undefined);
  const [isDsaAccepted, setIsDsaAccepted] = useState(undefined);
  const [dsaInfo, setDsaInfo] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(undefined);

  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [userInfoReady, setUserInfoReady] = useState(false);

  const refreshDsaStatus = useCallback(async () => {
    if (!isAuthenticated || !FUNCTION_URL) return;

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      const res = await fetch(`${FUNCTION_URL}/api/v1/users/dsa-status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(FUNCTION_KEY ? { 'x-functions-key': FUNCTION_KEY } : {})
        }
      });

      if (res.ok) {
        const { dsa } = await res.json();
        if (dsa) {
          setDsaInfo({
              accepted: dsa.accepted,
              accepted_at: dsa.accepted_at || null,
          });
          setIsDsaAccepted(Boolean(dsa.accepted));
        } else {
          setDsaInfo(null);
          setIsDsaAccepted(false);
        }
      } else {
        setIsDsaAccepted(false);
        setDsaInfo(null);
      }
    } catch (e) {
      console.warn('Could not query dsa-status:', e.message);
      setIsDsaAccepted(false);
      setDsaInfo(null);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    const getTokenAndClaims = async () => {
      if (isAuth0Loading) {
        return;
      }

      if (!isAuthenticated) {
        setUser(null);
        setDecodedToken(null);
        setUserPermissions([]);
        setIsValidated(false);
        setIsDsaAccepted(false);
        setDsaInfo(null);
        setIsEmailVerified(false);
        setAuthError(null);
        setUserInfoReady(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });

        const payload = JSON.parse(atob(token.split('.')[1]));
        setDecodedToken(payload);

        let enhancedUser = { ...auth0User };

        const perms = Array.isArray(payload.permissions) ? payload.permissions : [];
        setUserPermissions(perms);

        const hasValidatedPermission = perms.includes("validated:true") || perms.includes("validated");
        const validatedFromPayload = payload.validated === true || payload[`${USER_METADATA_NAMESPACE}validated`] === true;
        const validatedFromUser = getNamespacedClaim(enhancedUser, "validated") === true || enhancedUser?.validated === true;
        let validated = Boolean(hasValidatedPermission || validatedFromPayload || validatedFromUser);

        let dsaClaim = payload[`${USER_METADATA_NAMESPACE}dsa`] ?? getNamespacedClaim(enhancedUser, "dsa");
        let emailVerified = enhancedUser?.email_verified === true || payload.email_verified === true;

        const createdAtFromToken = payload[`${USER_METADATA_NAMESPACE}created_at`];
        const createdAtFromUser = getNamespacedClaim(enhancedUser, "created_at");
        
        enhancedUser.created_at = createdAtFromToken || createdAtFromUser || auth0User?.created_at;

        if (process.env.REACT_APP_AUTH0_DOMAIN) {
          try {
            const res = await fetch(`https://${process.env.REACT_APP_AUTH0_DOMAIN}/userinfo`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
              const userInfo = await res.json();
              
              // Capture created_at from userinfo if still missing
              if (!enhancedUser.created_at && userInfo.created_at) {
                enhancedUser.created_at = userInfo.created_at;
              }
              
              if (!enhancedUser.created_at && userInfo[`${USER_METADATA_NAMESPACE}created_at`]) {
                enhancedUser.created_at = userInfo[`${USER_METADATA_NAMESPACE}created_at`];
              }

              if (!validated) {
                validated = Boolean(
                  userInfo[`${USER_METADATA_NAMESPACE}validated`] === true ||
                  userInfo.validated === true
                );
              }
              if (!dsaClaim) {
                dsaClaim = userInfo[`${USER_METADATA_NAMESPACE}dsa`] ?? userInfo.dsa;
              }
              if (!emailVerified) {
                emailVerified = userInfo.email_verified === true;
              }
            }
          } catch (e) {
            console.warn("Error querying /userinfo:", e.message);
          }
        }

        setUser(enhancedUser);
        setIsValidated(Boolean(validated));
        setIsEmailVerified(Boolean(emailVerified));

        if (FUNCTION_URL) {
          try {
            const res = await fetch(`${FUNCTION_URL}/api/v1/users/dsa-status`, {
              headers: {
                Authorization: `Bearer ${token}`,
                ...(FUNCTION_KEY ? { 'x-functions-key': FUNCTION_KEY } : {})
              }
            });
            if (res.ok) {
              const { dsa } = await res.json();
              if (dsa) {
                setDsaInfo({
                  accepted: dsa.accepted,
                  rejected_at: dsa.rejected_at,
                  version: dsa.version || null,
                  accepted_at: dsa.accepted_at || null,
                });
                setIsDsaAccepted(Boolean(dsa.accepted));
              } else {
                setDsaInfo(null);
                setIsDsaAccepted(false);
              }
            } else {
              setIsDsaAccepted(false);
              setDsaInfo(null);
            }
          } catch (e) {
            console.warn('Could not query dsa-status:', e.message);
            setIsDsaAccepted(false);
            setDsaInfo(null);
          }
        } else {
          setIsDsaAccepted(false);
          setDsaInfo(null);
        }

        setAuthError(null);
        setUserInfoReady(true);
      } catch (e) {
        console.error("Error fetching access token or decoding claims", e);
        setAuthError(e);
        setDecodedToken(null);
        setUserPermissions([]);
        setIsValidated(false);
        setIsDsaAccepted(false);
        setDsaInfo(null);
        setIsEmailVerified(false);
        setUserInfoReady(true);
      } finally {
        setLoading(false);
      }
    };

    getTokenAndClaims();
  }, [isAuthenticated, isAuth0Loading, getAccessTokenSilently, auth0User]);

  return {
    user,
    decodedToken,
    userPermissions,
    isValidated,
    isDsaAccepted,
    dsaInfo,
    isEmailVerified,
    loading,
    authError,
    getNamespacedClaim,
    userInfoReady,
    refreshDsaStatus,
  };
};